import { Checkbox, Grid } from "@mantine/core";

import { getColValue } from "../../../pages/Reports/Report/Report.utils";
import { useSelector } from "../../../redux/hooks";
import {
  Flex,
  StyledTable,
  StyledTd,
  StyledTh,
  StyledTr,
} from "../../../styled";
import {
  formatDateTimeUTC,
  type INMEARow,
  type INMEASection,
  sortNMEARowsByTime,
} from "./NMEAAutomationModal.utils";

interface Props {
  section: INMEASection;
  sectionIndex: number;
  selectedSections: number[][];
  setSelectedSections: (selectedSections: number[][]) => void;
  onlyOneAllowed: boolean;
}

const NMEAAutomationModalPreview = ({
  section,
  sectionIndex,
  selectedSections,
  setSelectedSections,
  onlyOneAllowed,
}: Props) => {
  const { lbentry } = useSelector(state => state.generatorSlice);

  const isRowLockedFromUnselect = (row: INMEARow) => {
    const savedSection = lbentry?.NMEAData?.[section.originalSectionIndex];
    if (!savedSection?.rows?.length || !row.NMEAId) return false;

    const matchingSavedRow = savedSection.rows.find(
      savedRow => savedRow.NMEAId === row.NMEAId,
    );

    return Boolean(matchingSavedRow?.selected);
  };

  const handleToggle = (index: number) => {
    const updatedSections = [...selectedSections];
    const row = sortedRows[index];
    const isSelected = updatedSections[sectionIndex]?.includes(index);
    const isLocked = row ? isSelected && isRowLockedFromUnselect(row) : false;

    if (isLocked) return;

    if (isSelected) {
      updatedSections[sectionIndex] = updatedSections[sectionIndex].filter(
        i => i !== index,
      );
    } else {
      if (!updatedSections[sectionIndex]) {
        updatedSections[sectionIndex] = [];
      }
      if (onlyOneAllowed) {
        updatedSections[sectionIndex] = [];
      }
      updatedSections[sectionIndex].push(index);
    }
    setSelectedSections(updatedSections);
  };

  const hoverStyle = {
    transition: "transform 0.2s ease-in-out",
    "&:hover": {
      transform: "scale(1.03)",
      boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
      zIndex: 1,
      cursor: "pointer",
    },
  };

  const sortedRows = sortNMEARowsByTime(section.rows);

  return (
    <Grid columns={2} p={10}>
      {sortedRows.map((row, index) => {
        const isSelected = selectedSections[sectionIndex]?.includes(index);
        return (
          <Grid.Col
            span={1}
            onClick={() => handleToggle(index)}
            sx={hoverStyle}
            key={index}>
            <StyledTable>
              <StyledTr>
                <StyledTh colSpan={2}>
                  <Flex align="center" gap={30}>
                    <Checkbox
                      checked={isSelected}
                      onChange={() => handleToggle(index)}
                    />
                    <Flex direction="column" justify="flex-start" gap={0}>
                      {(row.dateTimeUTC ?? row.dateTimeLT) ? (
                        <>
                          {row.dateTimeLT && <Flex>{row.dateTimeLT}</Flex>}
                          {row.dateTimeUTC && <Flex>{row.dateTimeUTC}</Flex>}
                        </>
                      ) : (
                        formatDateTimeUTC(row.dateTime)
                      )}
                    </Flex>
                  </Flex>
                </StyledTh>
              </StyledTr>
              <StyledTr>
                <StyledTh>Column</StyledTh>
                <StyledTh>Data</StyledTh>
              </StyledTr>
              {Object.values(row.cols).map(
                (col, colIdx) =>
                  col.unit !== "24lock" && (
                    <StyledTr key={colIdx}>
                      <StyledTd>{col.name?.replaceAll("<br>", " ")}</StyledTd>
                      <StyledTd>{getColValue(col)}</StyledTd>
                    </StyledTr>
                  ),
              )}
            </StyledTable>
          </Grid.Col>
        );
      })}
    </Grid>
  );
};

export default NMEAAutomationModalPreview;
